// Clients and pure shaping for the daily history routes (C-6a):
// `GET /v1/classes/{class}/history` (one class's value by day) and
// `GET /v1/classes/history` (the all-class headline by day). Points are one
// per UTC day; a missing day stays missing, and a null value is dropped from
// the line, never drawn as zero. `backfill` points are estimates made later
// from that day's observations, so the chart draws them apart.

import { API_BASE, NotPublished } from "./api";
import type { Point } from "./stablecoins-view";

export type HistorySource = "snapshot" | "backfill" | "latest";

/** One token (or one chain of a token) that joined or left the tracked set. */
export interface CoverageItem {
  id: string;
  symbol: string | null;
  /** Value now (added) or on the previous day (removed), USD. */
  value_usd: number;
  /** Set when only this chain of an existing token was added or removed. */
  chain?: string;
  /** The item's class, on headline events. */
  class?: string;
}

/** What changed in the tracked set against the previous snapshot day (C-6a-fix). */
export interface CoverageEvent {
  date: string;
  since: string;
  /** Largest first, at most 10; the counts and values cover everything. */
  added: CoverageItem[];
  removed: CoverageItem[];
  added_count: number;
  removed_count: number;
  added_value_usd: number;
  removed_value_usd: number;
}

export interface ClassHistoryPoint {
  /** UTC date, YYYY-MM-DD. */
  date: string;
  as_of: string | null;
  value_usd: number | null;
  entries: number | null;
  priced: number | null;
  valued: number | null;
  source: HistorySource;
  /** That day's net flow of the class, USD (C-6c); null when not measured. */
  net_flow_usd?: number | null;
  /** Empty when nothing changed; null when not computed. */
  coverage_events?: CoverageEvent[] | null;
  /** DQ-16: "upstream_unconfirmed" when the day's figure holds an upstream value the jump guard has not confirmed. */
  quality?: string | null;
  /** How a backfill point was made: snapshot (estimated from observations), backfill_onchain, backfill_source. */
  basis?: string | null;
  /** Set by withLongHistory: a weekly point from the long series (the last stored day of its UTC week). */
  weekly?: boolean;
}

export interface ClassHistory {
  schema: "panther.class-history.v1";
  class: string;
  name: string;
  label: string;
  days: number;
  /** "1d", or "1w" when the series spans more than 730 days (each point the last stored day of its week). */
  interval: "1d" | "1w";
  sampling: string;
  value_note: string;
  /** How changes are measured: like for like, with coverage growth apart. */
  change_note?: string | null;
  /** First point's date; null when there is no history yet. */
  from: string | null;
  to: string | null;
  points: ClassHistoryPoint[];
}

export interface IndexHistoryPoint {
  date: string;
  as_of: string | null;
  /** The headline: each product counted once. */
  total_value_usd: number | null;
  gross_value_usd: number | null;
  overlap_value_usd: number | null;
  source: HistorySource;
  coverage_events?: CoverageEvent[] | null;
  net_flow_usd?: number | null;
  /** DQ-16: "upstream_unconfirmed" when the day's figure holds an upstream value the jump guard has not confirmed. */
  quality?: string | null;
  /** How a backfill point was made: snapshot (estimated from observations), backfill_onchain, backfill_source. */
  basis?: string | null;
  /** Set by withLongHistory: a weekly point from the long series (the last stored day of its UTC week). */
  weekly?: boolean;
}

export interface IndexHistory {
  interval?: string;
  schema: "panther.class-index-history.v1";
  days: number;
  from: string | null;
  to: string | null;
  change_note?: string | null;
  points: IndexHistoryPoint[];
}

/** `GET /v1/benchmarks/sofr`: SOFR by effective date from the NY Fed, rates as fractions (0.039 = 3.90%). */
export interface SofrBenchmark {
  schema: "panther.benchmark.v1";
  benchmark: "sofr";
  name: string;
  source: { name: string; url: string };
  as_of: string | null;
  latest: { date: string; rate: number } | null;
  /** The newest rate is more than 4 business days old. */
  stale: boolean;
  days: number;
  from: string | null;
  to: string | null;
  points: Array<{ date: string; rate: number; source: string }>;
}

/** The chart's range buttons, in days. */
export const HISTORY_RANGES = [
  { id: "7d", label: "7D", days: 7 },
  { id: "30d", label: "30D", days: 30 },
  { id: "90d", label: "90D", days: 90 },
  { id: "1y", label: "1Y", days: 365 },
] as const;

export type HistoryRangeId = (typeof HISTORY_RANGES)[number]["id"];

async function read<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { signal });
  if (response.status === 404) throw new NotPublished();
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  return (await response.json()) as T;
}

/** One class's value by day; `classApiId` as /v1/classes names it (treasury, govt_debt, stock…). */
export async function fetchClassHistory(classApiId: string, days: number, signal?: AbortSignal): Promise<ClassHistory> {
  const data = await read<ClassHistory>(`/v1/classes/${classApiId}/history?days=${days}`, signal);
  if (data?.schema !== "panther.class-history.v1" || !Array.isArray(data.points)) throw new Error("Unexpected history format");
  return data;
}

/** Daily resolution kept for the newest stretch: the API serves days, not weeks, up to 730 of them. */
const DAILY_DAYS = 730;

/**
 * Every stored point, with full detail where it matters: the API turns a series longer than 730
 * days into weekly points, so the newest 730 days are read daily and only the weeks before them
 * are kept from the long series (flagged `weekly`).
 */
export function withLongHistory<P extends { date: string }>(long: { points: P[]; interval?: string }, daily: { points: P[] } | null): Array<P & { weekly?: boolean }> {
  if (long.interval !== "1w" || !daily) return long.points;
  const start = daily.points.reduce<string | null>((min, p) => (min === null || p.date < min ? p.date : min), null);
  if (start === null) return long.points.map((p) => ({ ...p, weekly: true }));
  return [...long.points.filter((p) => p.date < start).map((p) => ({ ...p, weekly: true })), ...daily.points];
}

/** One class's whole history: weekly before the newest 730 days, daily within them. */
export async function fetchClassHistoryAll(classApiId: string, signal?: AbortSignal): Promise<ClassHistory> {
  const long = await read<ClassHistory>(`/v1/classes/${classApiId}/history?days=all`, signal);
  if (long?.schema !== "panther.class-history.v1" || !Array.isArray(long.points)) throw new Error("Unexpected history format");
  if (long.interval !== "1w") return long;
  const daily = await fetchClassHistory(classApiId, DAILY_DAYS, signal);
  return { ...daily, from: long.from, points: withLongHistory(long, daily) };
}

/** The headline's whole history, as fetchClassHistoryAll. */
export async function fetchIndexHistoryAll(signal?: AbortSignal): Promise<IndexHistory> {
  const long = await read<IndexHistory>(`/v1/classes/history?days=all`, signal);
  if (long?.schema !== "panther.class-index-history.v1" || !Array.isArray(long.points)) throw new Error("Unexpected history format");
  if (long.interval !== "1w") return long;
  const daily = await fetchIndexHistory(DAILY_DAYS, signal);
  return { ...daily, from: long.from, points: withLongHistory(long, daily) };
}

/** The all-class headline by day. */
export async function fetchIndexHistory(days: number, signal?: AbortSignal): Promise<IndexHistory> {
  const data = await read<IndexHistory>(`/v1/classes/history?days=${days}`, signal);
  if (data?.schema !== "panther.class-index-history.v1" || !Array.isArray(data.points)) throw new Error("Unexpected history format");
  return data;
}

/** SOFR for the last `days` calendar days (1 to 365). */
export async function fetchSofr(days: number, signal?: AbortSignal): Promise<SofrBenchmark> {
  const data = await read<SofrBenchmark>(`/v1/benchmarks/sofr?days=${days}`, signal);
  if (data?.schema !== "panther.benchmark.v1" || !Array.isArray(data.points)) throw new Error("Unexpected benchmark format");
  return data;
}

/** SOFR points as a chart line; every published rate is a real reading, none estimated. */
export function sofrSeries(data: Pick<SofrBenchmark, "points">): DailySeries {
  return dailySeries(
    data.points.map((p) => ({ date: p.date, rate: p.rate, source: "snapshot" as const, entries: null, coverage_events: null })),
    (p) => p.rate,
  );
}

/** What the chart can say about one drawn day beyond its value. */
export interface DayDetail {
  /** Entries counted that day; null when not reported. */
  entries: number | null;
  /** Products added to or removed from tracking that day. */
  events: CoverageEvent[];
  /** Value added minus value removed by those events, USD; null when the API did not compute them that day. */
  coverageUsd: number | null;
  /** DQ-16: the day's figure holds an upstream value not yet confirmed. */
  unconfirmed: boolean;
  /** How the point was estimated, for the tooltip ("reconstructed from chain state"); null for a real snapshot. */
  estimate: string | null;
  /** A weekly point (the last stored day of its week), from the long series. */
  weekly: boolean;
}

/**
 * Tooltip wording for an estimated point, from the history's source and basis: days before
 * snapshots began are estimated from observations (basis snapshot); earlier days are
 * reconstructed from chain state (backfill_onchain) or from source data such as DefiLlama's
 * per-coin charts (backfill_source). Null for a snapshot or today's figure.
 */
export function estimateLabel(source: HistorySource | string | null | undefined, basis: string | null | undefined): string | null {
  if (source !== "backfill") return null;
  if (basis === "backfill_onchain") return "reconstructed from chain state";
  if (basis === "backfill_source") return "reconstructed from source data";
  return "estimated from observations";
}

export interface DailySeries {
  /** [epoch ms at UTC midnight, value], oldest first; null values left out. */
  points: Point[];
  /** One per point, in the same order. */
  details: DayDetail[];
  /** Time of the last estimated (backfill) point drawn; null when none is. */
  estimatedThrough: number | null;
  /** The first drawn day, YYYY-MM-DD; null when nothing is drawn. */
  from: string | null;
}

const finite = (value: number | null | undefined): value is number => value !== null && value !== undefined && Number.isFinite(value);

/** Daily points as a chart line: readable days only, one per date, oldest first. */
export function dailySeries<
  P extends { date: string; source: HistorySource; entries?: number | null; valued?: number | null; coverage_events?: CoverageEvent[] | null; quality?: string | null; basis?: string | null; weekly?: boolean },
>(points: readonly P[], value: (point: P) => number | null): DailySeries {
  const byDay = new Map<number, { value: number; estimated: boolean; date: string; detail: DayDetail }>();
  let readableBefore = false;
  for (const point of [...points].sort((a, b) => a.date.localeCompare(b.date))) {
    const t = Date.parse(`${point.date}T00:00:00Z`);
    const v = value(point);
    if (!Number.isFinite(t) || !finite(v)) continue;
    let events = (point.coverage_events ?? []).filter((e) => e.added_count > 0 || e.removed_count > 0);
    let coverageUsd = point.coverage_events == null ? null : events.reduce((sum, e) => sum + e.added_value_usd - e.removed_value_usd, 0);
    // Every valued product "added" on a day after the class already had a value cannot be right (the
    // events were diffed against a day without per-token rows): the day's coverage is unknown, not all of it.
    const added = events.reduce((n, e) => n + e.added_count, 0);
    if (readableBefore && finite(point.valued) && point.valued > 0 && added >= point.valued) {
      events = [];
      coverageUsd = null;
    }
    readableBefore = true;
    byDay.set(t, { value: v, estimated: point.source === "backfill", date: point.date, detail: { entries: finite(point.entries) ? point.entries : null, events, coverageUsd, unconfirmed: point.quality === "upstream_unconfirmed", estimate: estimateLabel(point.source, point.basis), weekly: point.weekly === true } });
  }
  const days = [...byDay].sort((a, b) => a[0] - b[0]);
  let estimatedThrough: number | null = null;
  for (const [t, day] of days) if (day.estimated) estimatedThrough = t;
  return {
    points: days.map(([t, day]) => [t, day.value]),
    details: days.map(([, day]) => day.detail),
    estimatedThrough,
    from: days[0]?.[1].date ?? null,
  };
}

const usd = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(abs / 1e3).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "+15 products added to tracking ($3.66B)", "−2 removed from tracking ($40.0M)"; one line per direction. */
export function coverageSummary(event: CoverageEvent): string[] {
  const lines: string[] = [];
  if (event.added_count > 0) lines.push(`+${plural(event.added_count, "product", "products")} added to tracking (${usd(event.added_value_usd)})`);
  if (event.removed_count > 0) lines.push(`−${plural(event.removed_count, "product", "products")} removed from tracking (${usd(event.removed_value_usd)})`);
  return lines;
}

/** "syrupUSDC $1.04B", "USDC on Base $12.0M"; the API lists the largest ten. */
export function coverageItemText(item: CoverageItem): string {
  const name = item.symbol ?? item.id;
  return `${name}${item.chain ? ` on ${item.chain}` : ""} ${usd(item.value_usd)}`;
}

/** The hover text for a day's events: the summary, then the listed items, then how many more. */
export function coverageDetail(events: readonly CoverageEvent[]): string {
  const listed = (items: readonly CoverageItem[], total: number) => {
    const more = total - items.length;
    return `${items.map(coverageItemText).join(", ")}${more > 0 ? ` and ${more} more` : ""}`;
  };
  return events
    .flatMap((event) => [
      ...(event.added_count > 0 ? [`${coverageSummary({ ...event, removed_count: 0 })[0]}: ${listed(event.added, event.added_count)}`] : []),
      ...(event.removed_count > 0 ? [`${coverageSummary({ ...event, added_count: 0 })[0]}: ${listed(event.removed, event.removed_count)}`] : []),
    ])
    .join(". ");
}

/** True when the drawn line starts after the range does: the range asks for more days than exist. */
export function historyIsShort(series: Pick<DailySeries, "points">, days: number): boolean {
  const first = series.points[0];
  const last = series.points[series.points.length - 1];
  if (!first || !last) return true;
  return last[0] - first[0] < (days - 1) * 86_400_000;
}

/** "Oct 6, 2026". */
export function historyDate(date: string): string {
  const t = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(t)) return date;
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** A change chip's text, or null to hide it: never "0%" for an unknown change. */
export function changeChip(change: number | null | undefined): { text: string; tone: "up" | "down" | null } | null {
  if (!finite(change)) return null;
  const pct = change * 100;
  const digits = Math.abs(pct) >= 10 ? 0 : 1;
  const rounded = Number(pct.toFixed(digits));
  return { text: `${rounded > 0 ? "+" : ""}${rounded.toFixed(digits)}%`, tone: rounded > 0 ? "up" : rounded < 0 ? "down" : null };
}

/**
 * The reader-facing part of the API's change_note: its first sentence, which
 * the API keeps stable ("Changes compare the same products…"). The rest names
 * API fields.
 */
export function changeNoteText(note: string | null | undefined): string | null {
  const text = note?.trim();
  if (!text) return null;
  const end = text.indexOf(". ");
  return end === -1 ? text : text.slice(0, end + 1);
}
